/**
 * Shared contracts for durable source synchronization (source-sync).
 * Kept strictly free of backend Firebase SDK types and UI view models.
 */
export const SOURCE_SYNC_STATUSES = [
    'initializing',
    'pending',
    'syncing',
    'current',
    'degraded',
    'reconnect-required',
    'disconnected',
];
export function isSourceSyncStatus(val) {
    return typeof val === 'string' && SOURCE_SYNC_STATUSES.includes(val);
}
export function isValidSyncTarget(val) {
    if (!val || typeof val !== 'object')
        return false;
    const candidate = val;
    return (candidate.providerId === 'gmail' &&
        typeof candidate.connectionId === 'string' &&
        candidate.connectionId.trim().length > 0 &&
        candidate.resource === 'mail-threads');
}
/** Encodes each resource-key component independently so delimiters cannot collide. */
export function sourceSyncResourceKey(namespace, resourceType, resourceId) {
    const encode = (value) => {
        const bytes = new TextEncoder().encode(value);
        let binary = '';
        bytes.forEach((byte) => {
            binary += String.fromCharCode(byte);
        });
        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    };
    return `${encode(namespace)}:${encode(resourceType)}:${encode(resourceId)}`;
}
export function decodeSourceSyncResourceKey(key) {
    const parts = key.split(':');
    if (parts.length !== 3 || parts.some((part) => !part))
        return undefined;
    try {
        const decode = (value) => {
            const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
            const binary = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
            return new TextDecoder().decode(Uint8Array.from(binary, (char) => char.charCodeAt(0)));
        };
        const [namespace, resourceType, resourceId] = parts.map(decode);
        if (!namespace || !resourceType || !resourceId)
            return undefined;
        return { namespace, resourceType, resourceId };
    }
    catch {
        return undefined;
    }
}
/**
 * Extracts integer monotonic counter from revision string (e.g. "rev_12" -> 12, "rev_gmail_123" -> 123).
 */
export function parseRevisionNumber(revision) {
    if (!revision || typeof revision !== 'string')
        return 0;
    const match = revision.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
}
/**
 * Compares two revision strings numerically. Returns true if candidate is strictly newer than current.
 */
export function isRevisionNewer(candidate, current) {
    if (!current)
        return !!candidate;
    if (!candidate)
        return false;
    const candNum = parseRevisionNumber(candidate);
    const currNum = parseRevisionNumber(current);
    if (candNum !== currNum) {
        return candNum > currNum;
    }
    return candidate !== current;
}
//# sourceMappingURL=sourceSync.js.map